import {
  Alert,
  Breadcrumb,
  Button,
  Card,
  Col,
  Empty,
  message,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { FileTextOutlined, RightOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Pie } from "@ant-design/plots";
import View from "../../controls/View";
import { ALL_TIME } from "./FiscalPeriodSelect";
import AnalyticsScopeBar, {
  AnalyticsScope,
} from "../analytics/AnalyticsScopeBar";
import DomainGapTable from "./DomainGapTable";
import ComponentBreakdownTable from "./ComponentBreakdownTable";
import { renderVsTarget } from "../../code/reports/vsTarget";
import BandTag from "../../controls/BandTag";
import AnswerFormDialog from "../answers/AnswerFormDialog";
import { getProblemMessage } from "../../utils/http";
import {
  getAnswers,
  getQuestionnaireAnswerResults,
  getReportEvaluation,
  exportReportEvaluationPdf,
} from "../../repository/data.repository";
import {
  Answer,
  AssessmentReport,
  FiscalPeriod,
  MAX_PAGE_SIZE,
  QuestionnaireAnswerKPIResult,
  ReportComponentResult,
  ReportEvaluation,
} from "../../repository/data.default";
import {
  QueryCondition,
  QueryOperation,
} from "../../code/search-query/search-query";
import { groupByField, resolveRange } from "../analytics/AnalyticsRange";

// NOTE: cross-company analytics mode deferred (was in AssessmentResultsExplorer).

export interface ReportReviewProps {
  readonly open: boolean;
  readonly report: AssessmentReport | null;
  readonly onClose: () => void;
}

type Level = "aggregate" | "respondents" | "individual";

// Equality condition shorthand for the scoped per-answer reads.
const eq = (field: string, value: string): QueryCondition => ({
  field,
  operation: QueryOperation.Equal,
  values: [value],
});

function ReportReview(props: ReportReviewProps) {
  const reportUid = props.report?.uid;

  // Scope is owned by AnalyticsScopeBar; ReportReview just holds the resolved values.
  const [companyUid, setCompanyUid] = useState<string | undefined>();
  const [departmentUid, setDepartmentUid] = useState<string | undefined>();
  const [periodSel, setPeriodSel] = useState<string>(ALL_TIME);
  const [fiscalPeriods, setFiscalPeriods] = useState<FiscalPeriod[]>([]);

  // Level-1 aggregate
  const [evaluation, setEvaluation] = useState<ReportEvaluation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Drill state
  const [level, setLevel] = useState<Level>("aggregate");
  const [selectedKpi, setSelectedKpi] = useState<{
    uid: string;
    lineageUid?: string | null;
    name: string;
  } | null>(null);
  const [selectedRespondent, setSelectedRespondent] = useState<{
    answerUid: string;
    name: string;
  } | null>(null);

  // Per-respondent rows (level 2 + level 3 derive from these)
  const [respondents, setRespondents] = useState<QuestionnaireAnswerKPIResult[]>(
    [],
  );
  const [respLoading, setRespLoading] = useState(false);
  const [respError, setRespError] = useState<string | null>(null);

  // Level-3 individual rows (all KPIs for one answer)
  const [individual, setIndividual] = useState<QuestionnaireAnswerKPIResult[]>(
    [],
  );
  const [indLoading, setIndLoading] = useState(false);

  // Quick-preview of the actual answers a respondent gave (opens the shared AnswerFormDialog).
  const [answerToView, setAnswerToView] = useState<Answer | null>(null);
  const [answerLoading, setAnswerLoading] = useState(false);

  function resetDrill(): void {
    setLevel("aggregate");
    setSelectedKpi(null);
    setSelectedRespondent(null);
    setRespondents([]);
    setIndividual([]);
  }

  // Reset evaluation/drill on (re)open. Scope fields are owned + reset by AnalyticsScopeBar.
  useEffect(() => {
    if (!props.open || !reportUid) return;
    setEvaluation(null);
    setError(null);
    resetDrill();
  }, [props.open, reportUid]);

  // Receive resolved scope from the scope bar.
  const onScopeChange = useCallback((scope: AnalyticsScope) => {
    setCompanyUid(scope.companyUid);
    setDepartmentUid(scope.departmentUid);
    setPeriodSel(scope.periodSel);
    setFiscalPeriods(scope.fiscalPeriods);
  }, []);

  // Level-1: evaluate whenever scope changes. Drilling deeper is reset on scope change.
  useEffect(() => {
    if (!props.open || !reportUid || !companyUid) {
      setEvaluation(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    resetDrill();
    getReportEvaluation(
      reportUid,
      companyUid,
      periodSel === ALL_TIME ? undefined : periodSel,
      departmentUid,
    )
      .then((res) => {
        if (!cancelled) setEvaluation(res);
      })
      .catch((e) => {
        if (!cancelled) {
          setEvaluation(null);
          setError(getProblemMessage(e, "Could not evaluate this report."));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [props.open, reportUid, companyUid, departmentUid, periodSel]);

  // Build the scope conditions for the per-answer KPI-result reads (company/dept + period window).
  const scopeConditions = useMemo<QueryCondition[]>(() => {
    const conditions: QueryCondition[] = [];
    if (companyUid) conditions.push(eq("companyUid", companyUid));
    if (departmentUid) conditions.push(eq("departmentUid", departmentUid));
    const range = resolveRange(
      periodSel === ALL_TIME
        ? { mode: "all" }
        : { mode: "fiscal", fiscalPeriodUid: periodSel },
      fiscalPeriods,
    );
    if (range.start && range.end)
      conditions.push({
        field: "answerCreatedOn",
        operation: QueryOperation.Between,
        values: [range.start, range.end],
      });
    return conditions;
  }, [companyUid, departmentUid, periodSel, fiscalPeriods]);

  // Level-2: load per-respondent rows for the drilled KPI.
  useEffect(() => {
    if (level !== "respondents" || !selectedKpi) return;
    let cancelled = false;
    setRespLoading(true);
    setRespError(null);
    getQuestionnaireAnswerResults({
      searchQuery: {
        // Every respondent of the KPI, whichever version scored them — not only those on the version the row reads on.
        conditions: [
          ...scopeConditions,
          selectedKpi.lineageUid
            ? eq("kpiLineageUid", selectedKpi.lineageUid)
            : eq("kpiDefinitionUid", selectedKpi.uid),
        ],
      },
      pageNumber: 0,
      pageSize: MAX_PAGE_SIZE,
      sortDescending: false,
    })
      .then(({ data }) => {
        if (!cancelled) setRespondents(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setRespondents([]);
          setRespError(getProblemMessage(e, "Could not load respondents."));
        }
      })
      .finally(() => {
        if (!cancelled) setRespLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [level, selectedKpi, scopeConditions]);

  // Level-3: load all KPI results for the chosen respondent's answer.
  useEffect(() => {
    if (level !== "individual" || !selectedRespondent) return;
    let cancelled = false;
    setIndLoading(true);
    getQuestionnaireAnswerResults({
      searchQuery: {
        conditions: [eq("questionnaireAnswerUid", selectedRespondent.answerUid)],
      },
      pageNumber: 0,
      pageSize: MAX_PAGE_SIZE,
      sortDescending: false,
    })
      .then(({ data }) => {
        if (!cancelled) setIndividual(data);
      })
      .catch(() => {
        if (!cancelled) setIndividual([]);
      })
      .finally(() => {
        if (!cancelled) setIndLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [level, selectedRespondent]);

  function onExport(): void {
    if (!reportUid || !companyUid) return;
    setExporting(true);
    exportReportEvaluationPdf(
      reportUid,
      companyUid,
      periodSel === ALL_TIME ? undefined : periodSel,
      departmentUid,
    )
      .catch((e) =>
        message.error(getProblemMessage(e, "Could not export the report PDF.")),
      )
      .finally(() => setExporting(false));
  }

  function drillToKpi(row: ReportComponentResult): void {
    if (!row.inputUid || row.inputType !== "Kpi") return;
    setSelectedKpi({ uid: row.inputUid, lineageUid: row.inputLineageUid, name: row.label });
    setSelectedRespondent(null);
    setLevel("respondents");
  }

  function drillToRespondent(row: QuestionnaireAnswerKPIResult): void {
    setSelectedRespondent({
      answerUid: row.questionnaireAnswerUid,
      name: row.userUserName || "Respondent",
    });
    setLevel("individual");
  }

  // Fetch the full answer for the drilled respondent and open the read-only answer viewer.
  function openAnswers(): void {
    if (!selectedRespondent) return;
    setAnswerLoading(true);
    getAnswers({
      searchQuery: { conditions: [eq("uid", selectedRespondent.answerUid)] },
      pageNumber: 0,
      pageSize: 1,
    })
      .then(({ data }) => {
        if (data[0]) setAnswerToView(data[0]);
        else message.warning("Answer details are not available.");
      })
      .catch((e) =>
        message.error(getProblemMessage(e, "Could not load the answer.")),
      )
      .finally(() => setAnswerLoading(false));
  }

  const isGap = evaluation?.compositeOperator === "Gap";

  const bandDistribution = useMemo(
    () =>
      groupByField(respondents, "resultingRangeLabel").filter(
        (b) => b.type !== "—",
      ),
    [respondents],
  );

  const periodName = useMemo(() => {
    if (periodSel === ALL_TIME) return "All time";
    return fiscalPeriods.find((p) => p.uid === periodSel)?.name ?? "Period";
  }, [periodSel, fiscalPeriods]);

  // KPI / Score / Band / vs Target columns shared by Level-2 and Level-3 tables.
  // `drill` appends a chevron to signal the row is clickable (Level-2 → individual).
  const kpiResultColumns = (
    kpiCol: boolean,
    drill = false,
  ): ColumnsType<QuestionnaireAnswerKPIResult> => [
    kpiCol
      ? { title: "KPI", dataIndex: "kpiDefinitionName", key: "kpiDefinitionName" }
      : {
          title: "Respondent",
          dataIndex: "userUserName",
          key: "userUserName",
          render: (v?: string) => v || "—",
        },
    {
      title: "Score",
      dataIndex: "score",
      key: "score",
      width: 110,
      render: (v: number) => (v != null ? v.toFixed(2) : "—"),
    },
    {
      title: "Band",
      dataIndex: "resultingRangeLabel",
      key: "resultingRangeLabel",
      width: 180,
      render: (label: string | undefined, r: QuestionnaireAnswerKPIResult) => (
        <BandTag label={label} color={r.resultingRangeColor} />
      ),
    },
    {
      title: "vs Target",
      key: "vsTarget",
      width: 160,
      render: (_: unknown, r: QuestionnaireAnswerKPIResult) => renderVsTarget(r),
    },
    ...(drill
      ? [
          {
            title: "",
            key: "drill",
            width: 32,
            align: "center" as const,
            render: () => <RightOutlined className="text-slate-400" />,
          },
        ]
      : []),
  ];

  return (
    <View
      open={props.open}
      title={props.report ? `Review — ${props.report.name}` : "Review"}
      onBack={props.onClose}
      actions={
        <Button
          type="primary"
          loading={exporting}
          disabled={!companyUid || !evaluation}
          onClick={onExport}
        >
          Export PDF
        </Button>
      }
    >
      <Space direction="vertical" size="middle" className="flex w-full">
        {/* Persistent scope bar */}
        <Card size="small">
          <AnalyticsScopeBar
            onChange={onScopeChange}
            resetKey={props.open && reportUid}
          />
          {/* Scope echo — resolved scope names from the evaluation. */}
          {evaluation && (
            <Space wrap size={[4, 4]} className="mt-2">
              {evaluation.scopeCompanyName && (
                <Tag color="blue">{evaluation.scopeCompanyName}</Tag>
              )}
              {evaluation.scopeDepartmentName && (
                <Tag color="cyan">{evaluation.scopeDepartmentName}</Tag>
              )}
              <Tag color="geekblue">
                {evaluation.scopeFiscalPeriodName || periodName}
              </Tag>
            </Space>
          )}
        </Card>

        {!companyUid && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Select a company to review this report"
          />
        )}

        {companyUid && error && (
          <Alert type="error" showIcon message={error} />
        )}

        {companyUid && !error && (
          <Spin spinning={loading}>
            {/* Breadcrumb for drill levels */}
            <Breadcrumb
              className="mb-2"
              items={[
                {
                  title: (
                    <a
                      onClick={(e) => {
                        e.preventDefault();
                        setLevel("aggregate");
                        setSelectedRespondent(null);
                      }}
                    >
                      {props.report?.name ?? "Report"}
                    </a>
                  ),
                },
                ...(level !== "aggregate" && selectedKpi
                  ? [
                      {
                        title: (
                          <a
                            onClick={(e) => {
                              e.preventDefault();
                              setLevel("respondents");
                              setSelectedRespondent(null);
                            }}
                          >
                            {selectedKpi.name}
                          </a>
                        ),
                      },
                    ]
                  : []),
                ...(level === "individual" && selectedRespondent
                  ? [{ title: selectedRespondent.name }]
                  : []),
              ]}
            />

            {/* ---- Level 1: Aggregate ---- */}
            {level === "aggregate" && evaluation && (
              <>
                <Card size="small">
                  <Row align="middle" gutter={24}>
                    <Col>
                      <Statistic
                        title={evaluation.reportName}
                        value={
                          evaluation.value != null
                            ? evaluation.value.toFixed(1)
                            : "—"
                        }
                        suffix={
                          evaluation.value != null ? evaluation.unit ?? "" : ""
                        }
                      />
                    </Col>
                    <Col>
                      {evaluation.bandLabel ? (
                        <Tag
                          color={evaluation.bandColor ?? undefined}
                          style={{ fontSize: 14, padding: "4px 10px" }}
                        >
                          {evaluation.bandLabel}
                        </Tag>
                      ) : null}
                    </Col>
                    <Col flex="auto" className="text-right">
                      <Typography.Text type="secondary">
                        {evaluation.respondentCount} response(s) in scope
                      </Typography.Text>
                    </Col>
                  </Row>
                  {evaluation.bandInterpretation && (
                    <div className="mt-2">
                      <Typography.Text type="secondary">
                        Interpretation:{" "}
                      </Typography.Text>
                      <Typography.Text>
                        {evaluation.bandInterpretation}
                      </Typography.Text>
                    </div>
                  )}
                  {evaluation.bandRecommendedAction && (
                    <div>
                      <Typography.Text type="secondary">
                        Recommended action:{" "}
                      </Typography.Text>
                      <Typography.Text>
                        {evaluation.bandRecommendedAction}
                      </Typography.Text>
                    </div>
                  )}
                  {evaluation.note && (
                    <Alert
                      className="mt-2"
                      type="info"
                      message={evaluation.note}
                      showIcon
                    />
                  )}
                  {evaluation.versionNotice && (
                    <Alert
                      className="mt-2"
                      type="warning"
                      message="Mixed questionnaire versions"
                      description={evaluation.versionNotice}
                      showIcon
                    />
                  )}
                </Card>

                {isGap ? (
                  <DomainGapTable domainGaps={evaluation.domainGaps} />
                ) : (
                  <ComponentBreakdownTable
                    components={evaluation.components}
                    onDrillKpi={drillToKpi}
                    unit={evaluation.unit}
                  />
                )}
              </>
            )}

            {level === "aggregate" && !evaluation && !loading && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No evaluation available for this scope"
              />
            )}

            {/* ---- Level 2: Respondents ---- */}
            {level === "respondents" && selectedKpi && (
              <>
                {respError && (
                  <Alert type="error" showIcon message={respError} />
                )}
                <Spin spinning={respLoading}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} lg={16}>
                      <Card size="small" title={`Respondents — ${selectedKpi.name}`}>
                        <Table<QuestionnaireAnswerKPIResult>
                          size="small"
                          rowKey="uid"
                          pagination={false}
                          dataSource={respondents}
                          locale={{ emptyText: "No respondents for this scope" }}
                          onRow={(r) => ({
                            onClick: () => drillToRespondent(r),
                            style: { cursor: "pointer" },
                          })}
                          columns={kpiResultColumns(false, true)}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} lg={8}>
                      <Card size="small" title="Band distribution">
                        {bandDistribution.length ? (
                          <Pie
                            data={bandDistribution}
                            height={260}
                            angleField="value"
                            colorField="type"
                            radius={0.85}
                            innerRadius={0.5}
                            tooltip={false}
                            legend={{
                              color: {
                                title: false,
                                position: "bottom",
                                rowPadding: 5,
                              },
                            }}
                          />
                        ) : (
                          <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="No data"
                          />
                        )}
                      </Card>
                    </Col>
                  </Row>
                </Spin>
              </>
            )}

            {/* ---- Level 3: Individual ---- */}
            {level === "individual" && selectedRespondent && (
              <Spin spinning={indLoading}>
                <Card
                  size="small"
                  title={`Assessment results — ${selectedRespondent.name}`}
                  extra={
                    <Button
                      size="small"
                      icon={<FileTextOutlined />}
                      loading={answerLoading}
                      onClick={openAnswers}
                    >
                      View answers
                    </Button>
                  }
                >
                  <Table<QuestionnaireAnswerKPIResult>
                    size="small"
                    rowKey="uid"
                    pagination={false}
                    dataSource={individual}
                    locale={{ emptyText: "Not yet scored" }}
                    columns={kpiResultColumns(true)}
                  />
                </Card>
              </Spin>
            )}
          </Spin>
        )}
      </Space>

      {answerToView && (
        <AnswerFormDialog
          open
          mode="view"
          initialValues={answerToView}
          onCancel={() => setAnswerToView(null)}
          onFinish={() => setAnswerToView(null)}
        />
      )}
    </View>
  );
}

export default ReportReview;
